import React, { createContext, ReactNode, useContext, useMemo } from "react";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  UseMutationOptions,
} from "@tanstack/react-query";
import { ApiClient, createApi } from "~/backendApi/index";
import { useAuthState } from "./authContext";

type ApiValue = {
  readonly client: ApiClient;
  readonly baseUrl: string;
};

const Context = createContext<ApiValue | undefined>(undefined);

type ApiProviderProps = {
  readonly baseUrl: string;
  readonly children: ReactNode;
};

function ApiProvider({ baseUrl, children }: ApiProviderProps) {
  const authState = useAuthState();
  const value = useMemo(() => {
    if (authState.type === "signedIn") {
      return {
        baseUrl,
        client: createApi(baseUrl, authState.signOut, authState.user.authToken),
      };
    }
    return {
      baseUrl,
      client: createApi(baseUrl, () => {}),
    };
  }, [baseUrl, authState]);
  const queryClient = useMemo(() => new QueryClient(), []);

  return (
    <Context.Provider value={value}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </Context.Provider>
  );
}

function useApiClient() {
  const value = useContext(Context);
  if (value === undefined) {
    throw new Error("No api context");
  }
  return value.client;
}

function useApiBaseUrl() {
  const value = useContext(Context);
  if (value === undefined) {
    throw new Error("No api context");
  }
  return value.baseUrl;
}

function useApiMutation<TVariables, TData = unknown, TContext = unknown>(
  call: (client: ApiClient, input: TVariables) => Promise<TData>,
  options?: Omit<
    UseMutationOptions<TData, unknown, TVariables, TContext>,
    "mutationFn"
  >,
) {
  const apiClient = useApiClient();
  return useMutation((input: TVariables) => {
    return call(apiClient, input);
  }, options);
}

export { useApiClient, useApiBaseUrl, useApiMutation, ApiProvider };
